import React, { useState, useEffect } from 'react';
import { suppliersService } from '../../../services/suppliersService';
import TemplateCard from './TemplateCard';
import Modal from '../../common/Modal';
import './ComponentLibrary.css';

const ComponentLibraryModal = ({ isOpen, onClose, onSelectTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Component categories for filtering
  const categories = [
    { id: '', name: 'All Categories' },
    { id: 'packages', name: 'Packages' },
    { id: 'theme_park_packages', name: 'Theme Park Packages' },
    { id: 'cruise_packages', name: 'Cruise Packages' },
    { id: 'hotel_packages', name: 'Hotel Packages' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      fetchSuppliers();
    }
  }, [isOpen]);

  useEffect(() => {
    filterTemplates();
  }, [templates, selectedSupplier, selectedCategory, searchTerm]);

  const fetchSuppliers = async () => {
    try {
      const suppliersData = await suppliersService.getAll();
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // For now, we'll use static templates. Later this can be dynamic from backend
      const staticTemplates = [
        {
          id: 'disney-wdw-resort-package',
          name: 'Walt Disney World Resort Package',
          supplier: 'Disney',
          category: 'theme_park_packages',
          description: 'Complete Disney World vacation package with resort, tickets, and add-ons',
          image: '/images/disney/disney-world-castle.jpg',
          template_data: {
            component_type: 'theme_park_package',
            title: 'Walt Disney World Resort Package',
            // All the fields we defined will be here
            check_in_date: '',
            check_out_date: '',
            adults: 2,
            children: [],
            resort_name: '',
            room_type: '',
            view: '',
            ticket_type: '5-Day Ticket',
            ticket_options: 'Park Hopper Option',
            ticket_validity_start: '',
            ticket_validity_end: '',
            dining_plan: 'No Dining Plan',
            memory_maker: 'No',
            ground_transportation: '',
            travel_protection: 'Not Included',
            travel_protection_price: 0,
            total_price: 0,
            total_price_due_by: '',
            deposit_due: 0,
            deposit_due_by: '',
            special_requests: '',
            booking_notes: '',
            confirmation_number: ''
          }
        },
        {
          id: 'universal-orlando-package',
          name: 'Universal Orlando Resort Package',
          supplier: 'Universal',
          category: 'theme_park_packages',
          description: 'Universal Orlando vacation with resort, park tickets, and Express Pass options',
          image: '/images/disney/disney-world-resort-1.jpg', // Reusing existing image
          template_data: {
            component_type: 'theme_park_package',
            title: 'Universal Orlando Resort Package',
            check_in_date: '',
            check_out_date: '',
            adults: 2,
            children: [],
            resort_name: '',
            room_type: '',
            ticket_type: '3-Day Park-to-Park',
            express_pass: 'Not Included',
            total_price: 0,
            deposit_due: 0
          }
        },
        {
          id: 'cruise-caribbean-7day',
          name: '7-Day Caribbean Cruise',
          supplier: 'Royal Caribbean',
          category: 'cruise_packages',
          description: 'Western Caribbean cruise with interior stateroom and dining package',
          image: '/images/disney/disney-world-resort-2.jpg', // Reusing existing image
          template_data: {
            component_type: 'cruise',
            title: '7-Day Caribbean Cruise',
            departure_date: '',
            return_date: '',
            ship_name: '',
            stateroom_category: 'Interior',
            guests: 2,
            dining_package: 'Traditional',
            beverage_package: 'Not Included',
            total_price: 0,
            deposit_due: 0
          }
        },
        {
          id: 'hotel-deluxe-resort',
          name: 'Deluxe Resort Hotel',
          supplier: 'Marriott',
          category: 'hotel_packages',
          description: 'Luxury resort accommodation with breakfast and resort credits',
          image: '/images/default-template.jpg',
          template_data: {
            component_type: 'hotel',
            title: 'Deluxe Resort Hotel',
            check_in_date: '',
            check_out_date: '',
            room_type: 'Deluxe Room',
            guests: 2,
            meal_plan: 'Breakfast Included',
            resort_credit: 100,
            total_price: 0,
            deposit_due: 0
          }
        }
      ];
      setTemplates(staticTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    // Filter by supplier
    if (selectedSupplier) {
      filtered = filtered.filter(template => 
        template.supplier.toLowerCase() === selectedSupplier.toLowerCase()
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(template => 
        template.category === selectedCategory
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleSelectTemplate = (template) => {
    onSelectTemplate(template);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="📚 Component Library" maxWidth="1200px">
        <div className="library-filters">
          <div className="filter-row">
            <div className="filter-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input"
              />
            </div>
            
            <div className="filter-group">
              <label>Supplier:</label>
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className="filter-select"
              >
                <option value="">All Suppliers</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.name}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Category:</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="library-content">
          {loading ? (
            <div className="loading-state">
              <p>Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="empty-state">
              <p>No templates found matching your criteria.</p>
            </div>
          ) : (
            <div className="templates-grid">
              {filteredTemplates.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={() => handleSelectTemplate(template)}
                />
              ))}
            </div>
          )}
        </div>
    </Modal>
  );
};

export default ComponentLibraryModal;
